/**
 * Pure helpers for workflow tickets (human-gated agents: NUP today, more later).
 *
 * Shared stage machine + approval contract used by ticket detail, Approvals, and chat.
 * Per-workflow presentation lives in `workflowTypes.js` so new agents can register
 * without rewriting the Approvals page.
 */

import {
  isNupTicketCandidate,
  isTerTicketCandidate,
  isFfidTicketCandidate,
  isPamterTicketCandidate,
  isAccessModTicketCandidate,
  resolveWorkflowType,
  WORKFLOW_TYPE_NUP,
  WORKFLOW_TYPE_TER,
  WORKFLOW_TYPE_PWD_RESET,
  WORKFLOW_TYPE_FFID,
  WORKFLOW_TYPE_ACCESS_MOD,
  WORKFLOW_TYPE_PAMTER,
} from '@/lib/workflowTypes';

export {
  WORKFLOW_TYPE_NUP,
  WORKFLOW_TYPE_TER,
  WORKFLOW_TYPE_PWD_RESET,
  WORKFLOW_TYPE_FFID,
  WORKFLOW_TYPE_ACCESS_MOD,
  WORKFLOW_TYPE_PAMTER,
  WORKFLOW_TYPE_CATALOG,
  resolveWorkflowType,
  registerWorkflowType,
} from '@/lib/workflowTypes';

export const WORKFLOW_STAGE = {
  EXTRACTING: 'EXTRACTING',
  CHECKING: 'CHECKING',
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  NEEDS_INFO: 'NEEDS_INFO',
  APPROVED: 'APPROVED',
  PROVISIONING: 'PROVISIONING',
  TERMINATING: 'TERMINATING',
  OFFBOARDING: 'OFFBOARDING',
  SCHEDULED: 'SCHEDULED',
  AWAITING_USER_CONFIRMATION: 'AWAITING_USER_CONFIRMATION',
  ACCESS_ISSUE_REPORTED: 'ACCESS_ISSUE_REPORTED',
  DONE: 'DONE',
  REJECTED: 'REJECTED',
  FAILED: 'FAILED',
  ESCALATED: 'ESCALATED',
};

export const WORKFLOW_STAGE_LABELS = {
  [WORKFLOW_STAGE.EXTRACTING]: 'Extracting request',
  [WORKFLOW_STAGE.CHECKING]: 'Checking access',
  [WORKFLOW_STAGE.PENDING_APPROVAL]: 'Pending Approval',
  [WORKFLOW_STAGE.NEEDS_INFO]: 'Needs Information',
  [WORKFLOW_STAGE.APPROVED]: 'Approved — assignment pending',
  [WORKFLOW_STAGE.PROVISIONING]: 'Provisioning',
  [WORKFLOW_STAGE.TERMINATING]: 'Terminating',
  [WORKFLOW_STAGE.OFFBOARDING]: 'Offboarding',
  [WORKFLOW_STAGE.SCHEDULED]: 'Scheduled',
  [WORKFLOW_STAGE.AWAITING_USER_CONFIRMATION]: 'Awaiting User Confirmation',
  [WORKFLOW_STAGE.ACCESS_ISSUE_REPORTED]: 'Human Escalation — Login Issue',
  [WORKFLOW_STAGE.DONE]: 'Completed',
  [WORKFLOW_STAGE.REJECTED]: 'Rejected',
  [WORKFLOW_STAGE.FAILED]: 'Failed',
  [WORKFLOW_STAGE.ESCALATED]: 'Escalated — Human Review',
};

/** End-user email confirmation payload from workflow_state (top-level preferred). */
export function getUserConfirmation(ticket) {
  const state = getWorkflowState(ticket);
  const top = state.user_confirmation;
  if (top && typeof top === 'object') {
    // Prefer top-level when it carries an outcome (response), even if nested lags.
    if (top.response || top.status) return top;
  }
  const nested = state.provisioning?.user_confirmation;
  if (nested && typeof nested === 'object') return nested;
  if (top && typeof top === 'object') return top;
  return null;
}

/**
 * Human label for a workflow stage, including confirmation outcome when present.
 * Prefer this over raw WORKFLOW_STAGE_LABELS when a ticket object is available.
 */
export function getWorkflowStageLabel(ticketOrStage, maybeTicket) {
  let stage = '';
  let ticket = null;
  if (typeof ticketOrStage === 'string' || ticketOrStage == null) {
    stage = String(ticketOrStage || '');
    ticket = maybeTicket || null;
  } else {
    ticket = ticketOrStage;
    stage = String(ticket?.workflow_stage || '');
  }

  // List API may send a precomputed badge (TicketSummary has no full workflow_state).
  const precomputed = String(ticket?.workflow_badge || '').trim();
  if (precomputed) return precomputed;

  const uc = getUserConfirmation(ticket);
  const response = String(uc?.response || uc?.status || '').toUpperCase();
  const typeId = ticket ? resolveWorkflowType(ticket).id : '';
  const usesLoginConfirmation =
    typeId === WORKFLOW_TYPE_NUP || typeId === WORKFLOW_TYPE_PWD_RESET;

  if (stage === WORKFLOW_STAGE.DONE || response === 'SUCCESS') {
    // Only NUP / password-reset wait for end-user login confirmation.
    if (usesLoginConfirmation) {
      return 'Access confirmed';
    }
    if (ticket && resolveWorkflowType(ticket).id === WORKFLOW_TYPE_FFID) {
      return 'FFID Assignment Confirmed';
    }
    return WORKFLOW_STAGE_LABELS[WORKFLOW_STAGE.DONE] || 'Completed';
  }
  if (
    stage === WORKFLOW_STAGE.ACCESS_ISSUE_REPORTED ||
    response === 'ISSUE' ||
    response === 'ISSUE_REPORTED'
  ) {
    if (ticket && resolveWorkflowType(ticket).id === WORKFLOW_TYPE_FFID) {
      const hold = getFfidNotifyHoldReason(ticket);
      if (hold === 'missing_notify_email') return 'Human Escalation — email missing';
      if (hold === 'email_send_failed') return 'Human Escalation — email sending failed';
      if (hold === 'future_window') return 'Human Escalation — waiting for access date';
      if (response === 'ISSUE' || response === 'ISSUE_REPORTED') {
        return 'Human Escalation — access issue';
      }
      return 'Human Escalation — follow-up needed';
    }
    return WORKFLOW_STAGE_LABELS[WORKFLOW_STAGE.ACCESS_ISSUE_REPORTED];
  }
  if (stage === WORKFLOW_STAGE.NEEDS_INFO && isFfidAlreadyAssigned(ticket)) {
    return 'Firefighter already assigned';
  }
  if (stage === WORKFLOW_STAGE.NEEDS_INFO && isFfidNoIdAvailable(ticket)) {
    return 'No Firefighter ID available';
  }
  if (stage === WORKFLOW_STAGE.NEEDS_INFO && isFfidFormMissing(ticket)) {
    return 'Form missing';
  }
  if (stage === WORKFLOW_STAGE.SCHEDULED && ticket && resolveWorkflowType(ticket).id === WORKFLOW_TYPE_FFID) {
    return 'Human Escalation — waiting for access date';
  }
  if (stage === WORKFLOW_STAGE.NEEDS_INFO && isFfidUnresolvedWindow(ticket)) {
    return 'Invalid access window';
  }
  if (stage === WORKFLOW_STAGE.FAILED) {
    const reason = String(getWorkflowState(ticket).block_reason || '');
    if (reason === 'identity_unverified') {
      return 'SAP identity could not be verified';
    }
    if (reason === 'pool_unavailable') {
      return 'Firefighter ID pool unavailable';
    }
  }
  return WORKFLOW_STAGE_LABELS[stage] || stage || 'In progress';
}

/** Canonical NUP steps — used as a fallback when workflow_state has no steps yet. */
export function defaultNupSteps() {
  return [
    { key: 'parse_request', label: 'Parse request', status: 'pending' },
    { key: 'fetch_roles', label: 'Fetch reference user roles', status: 'pending' },
    { key: 'sod_check', label: 'Run SoD conflict check', status: 'pending' },
    { key: 'await_approval', label: 'Summarize & request approval', status: 'pending' },
    { key: 'create_user', label: 'Create SAP user', status: 'pending' },
    { key: 'copy_roles', label: 'Copy reference user roles', status: 'pending' },
    { key: 'send_email', label: 'Email credentials to user', status: 'pending' },
    { key: 'await_user_confirmation', label: 'Await user confirmation', status: 'pending' },
    { key: 'complete', label: 'Complete', status: 'pending' },
  ];
}

/** Canonical TER steps — fallback when workflow_state has no steps yet. */
export function defaultTerSteps(mode) {
  const terminateLabel =
    mode === 'scheduled'
      ? 'Schedule deactivation (set validity-to)'
      : 'Revoke roles & terminate SAP user';
  return [
    { key: 'parse_request', label: 'Parse request', status: 'pending' },
    { key: 'check_user_exists', label: 'Check user exists & status', status: 'pending' },
    { key: 'await_approval', label: 'Summarize & request approval', status: 'pending' },
    { key: 'terminate_user', label: terminateLabel, status: 'pending' },
    { key: 'validate_termination', label: 'Validate termination', status: 'pending' },
    { key: 'complete', label: 'Complete', status: 'pending' },
  ];
}

/** Canonical Password Reset steps — mirrors pwd_reset.py's default_pwd_reset_steps(). */
export function defaultPwdResetSteps() {
  return [
    { key: 'parse_request', label: 'Parse request', status: 'pending' },
    { key: 'reset_password', label: 'Reset SAP password', status: 'pending' },
    { key: 'send_email', label: 'Email new password to user', status: 'pending' },
    { key: 'await_user_confirmation', label: 'Await user confirmation', status: 'pending' },
    { key: 'complete', label: 'Complete', status: 'pending' },
  ];
}

/** Canonical FFID steps — mirrors ffid_agent_client.default_ffid_steps(). */
export function defaultFfidSteps() {
  return [
    { key: 'parse_request', label: 'Parse FFID form', status: 'pending' },
    { key: 'validate_request', label: 'Validate required fields', status: 'pending' },
    { key: 'resolve_identity', label: 'Resolve Firefighter SAP user', status: 'pending' },
    { key: 'resolve_dates', label: 'Resolve access window', status: 'pending' },
    { key: 'get_ffid_pool', label: 'Load GRC FFID pool', status: 'pending' },
    { key: 'select_candidate', label: 'Choose proposed FFID (available + required access)', status: 'pending' },
    { key: 'tcode_check', label: 'Validate requested T-codes on available FFIDs', status: 'pending' },
    { key: 'await_approval', label: 'Summarize & request HALO approval', status: 'pending' },
    { key: 'recheck_availability', label: 'Re-check availability (eligible FFIDs only)', status: 'pending' },
    { key: 'assign_ffid', label: 'Assign Firefighter ID in GRC', status: 'pending' },
    { key: 'verify_assignment', label: 'Verify GRC assignment', status: 'pending' },
    { key: 'set_validity', label: 'Set SU01 validity', status: 'pending' },
    { key: 'unlock_ffid', label: 'Unlock Firefighter ID', status: 'pending' },
    { key: 'send_email', label: 'Email assignment notice to user', status: 'pending' },
    { key: 'await_user_confirmation', label: 'Await user confirmation', status: 'pending' },
    { key: 'complete', label: 'Complete', status: 'pending' },
  ];
}

/** User-facing FFID React Flow milestones. Technical child keys stay on the ticket for audit. */
export const FFID_FLOW_MILESTONES = [
  { key: 'request_intake', label: 'Request Intake', children: ['parse_request'] },
  {
    key: 'ffid_analysis',
    label: 'FFID Analysis',
    children: [
      'validate_request',
      'resolve_identity',
      'resolve_dates',
      'get_ffid_pool',
      'select_candidate',
      'tcode_check',
    ],
  },
  { key: 'approval', label: 'Approval', children: ['await_approval'] },
  {
    key: 'grc_assignment',
    label: 'GRC/NWBC Assignment',
    children: [
      'recheck_availability',
      'assign_ffid',
      'verify_assignment',
      // leftover v4.6 keys — no SU01 node; fold into GRC so old tickets stay mapped
      'set_validity',
      'unlock_ffid',
    ],
  },
  { key: 'user_notification', label: 'User Notification', children: ['send_email'] },
  {
    key: 'await_user_confirmation',
    label: 'Await User Confirmation',
    children: ['await_user_confirmation'],
  },
  { key: 'complete', label: 'Complete', children: ['complete'] },
];

function _childStatuses(milestone, byKey) {
  return milestone.children.map((key) => String(byKey[key]?.status || 'pending').toLowerCase());
}

export function ffidMilestoneKeyForStep(stepKey) {
  const raw = String(stepKey || '').trim();
  const match = FFID_FLOW_MILESTONES.find((row) => row.key === raw || row.children.includes(raw));
  return match?.key || raw || null;
}

/**
 * Roll technical FFID steps into the GRC-only business milestones for React Flow.
 * Detailed child statuses remain on workflow_state.steps / the audit package.
 */
export function collapseFfidSteps(steps, { currentStep, workflowStage, windowKind, notifyHold } = {}) {
  const byKey = {};
  (Array.isArray(steps) ? steps : []).forEach((step) => {
    if (step && typeof step === 'object' && step.key) byKey[step.key] = step;
  });
  const stage = String(workflowStage || '').toUpperCase();
  const finished =
    stage === WORKFLOW_STAGE.DONE ||
    String(byKey.complete?.status || '').toLowerCase() === 'done';
  const futureHold =
    String(notifyHold || '') === 'future_window' ||
    (String(windowKind || '').toLowerCase() === 'future' &&
      (stage === WORKFLOW_STAGE.ACCESS_ISSUE_REPORTED || stage === WORKFLOW_STAGE.SCHEDULED));
  let activeIndex = FFID_FLOW_MILESTONES.findIndex(
    (row) => row.key === currentStep || row.children.includes(currentStep),
  );
  if (activeIndex < 0) {
    activeIndex = FFID_FLOW_MILESTONES.findIndex((row) =>
      _childStatuses(row, byKey).some((status) => status === 'active'),
    );
  }
  if (futureHold && !finished && String(byKey.complete?.status || '').toLowerCase() !== 'done') {
    activeIndex = FFID_FLOW_MILESTONES.findIndex((row) => row.key === 'await_user_confirmation');
  }
  if (finished) activeIndex = FFID_FLOW_MILESTONES.length - 1;

  return FFID_FLOW_MILESTONES.map((milestone, index) => {
    const statuses = _childStatuses(milestone, byKey);
    let status = 'pending';
    if (statuses.some((s) => s === 'failed')) status = 'failed';
    else if (finished || (activeIndex >= 0 && index < activeIndex)) status = 'done';
    else if (activeIndex === index) {
      status = statuses.every((s) => s === 'done') ? 'done' : 'active';
    } else if (statuses.every((s) => s === 'skipped') && !futureHold) {
      status = 'skipped';
    } else if (statuses.every((s) => s === 'done' || s === 'skipped') && statuses.some((s) => s === 'done')) {
      status = 'done';
    }
    return { key: milestone.key, label: milestone.label, status };
  });
}

const NUP_STEP_ORDER = [
  'parse_request',
  'fetch_roles',
  'sod_check',
  'await_approval',
  'create_user',
  'copy_roles',
  'send_email',
  'await_user_confirmation',
  'complete',
];

const NUP_STEP_LABELS = {
  parse_request: 'Parse request',
  fetch_roles: 'Fetch reference user roles',
  sod_check: 'Run SoD conflict check',
  await_approval: 'Summarize & request approval',
  create_user: 'Create SAP user',
  copy_roles: 'Copy reference user roles',
  send_email: 'Email credentials to user',
  await_user_confirmation: 'Await user confirmation',
  complete: 'Complete',
};

/** Drop legacy provision node and keep Complete at the end of the graph. */
export function normalizeNupSteps(steps) {
  const list = Array.isArray(steps) ? steps : [];
  const byKey = {};
  const extras = [];
  list.forEach((step) => {
    if (!step || typeof step !== 'object') return;
    const key = String(step.key || '').trim();
    if (!key || key === 'provision') return;
    if (NUP_STEP_ORDER.includes(key)) {
      byKey[key] = { ...(byKey[key] || {}), ...step };
    } else {
      extras.push(step);
    }
  });

  let out = NUP_STEP_ORDER.filter((key) => byKey[key]).map((key) => ({
    ...byKey[key],
    key,
    label: byKey[key].label || NUP_STEP_LABELS[key] || key,
  }));

  if (out.some((s) => ['create_user', 'copy_roles', 'send_email', 'complete'].includes(s.key))) {
    const keys = new Set(out.map((s) => s.key));
    ['await_user_confirmation', 'complete'].forEach((key) => {
      if (!keys.has(key)) {
        out.push({ key, label: NUP_STEP_LABELS[key], status: 'pending' });
        keys.add(key);
      }
    });
    out = out
      .filter((s) => NUP_STEP_ORDER.includes(s.key))
      .sort((a, b) => NUP_STEP_ORDER.indexOf(a.key) - NUP_STEP_ORDER.indexOf(b.key));
  }

  return out.concat(extras);
}

/** True when agent-produced workflow metadata requires the workflow detail view. */
export function isWorkflowTicket(ticket) {
  if (!ticket) return false;
  if (ticket.workflow_stage || ticket.workflow_agent) return true;
  const state = getWorkflowState(ticket);
  if (
    isNupTicketCandidate(ticket) ||
    isTerTicketCandidate(ticket) ||
    isFfidTicketCandidate(ticket) ||
    isPamterTicketCandidate(ticket) ||
    isAccessModTicketCandidate(ticket)
  ) {
    return true;
  }
  return Boolean(
    state &&
      (state.workflow_type ||
        state.workflowType ||
        state.current_step ||
        state.approval ||
        (Array.isArray(state.steps) && state.steps.length > 0)),
  );
}

/** True when this ticket exists only to back Approvals for a Vibe chat workflow run. */
export function isChatMirroredWorkflowTicket(ticket) {
  if (!ticket) return false;
  const tn = String(ticket.ticket_number || ticket.id || '').trim();
  if (/^CHAT-(NUP|TER)-/i.test(tn)) return true;
  const state = getWorkflowState(ticket);
  if (state.mirrored_from_chat || state.chat_session_id) return true;
  const email = String(ticket.requester_email || '').trim().toLowerCase();
  if (email === 'vibe-chat@localhost') return true;
  return false;
}

export function isPendingApproval(ticket) {
  return (ticket?.workflow_stage || '') === WORKFLOW_STAGE.PENDING_APPROVAL;
}

export function getFfidHeldIds(ticket) {
  const state = getWorkflowState(ticket);
  return (state.already_assigned || [])
    .map((row) => row?.ffid)
    .filter(Boolean);
}

/** Policy stop: Firefighter already has an overlapping FFID. Not missing fields. */
export function isFfidAlreadyAssigned(ticket) {
  const state = getWorkflowState(ticket);
  return (
    state.block_reason === 'already_assigned'
    || state.selection_outcome === 'already_assigned'
  );
}

/** Pool has no free FFID for the requested window. Not missing form fields. */
export function isFfidNoIdAvailable(ticket) {
  const state = getWorkflowState(ticket);
  if (state.selection_outcome === 'no_available' || state.block_reason === 'no_available') {
    return true;
  }
  const missing = state.missing_fields || [];
  return missing.some((m) => /no available firefighter/i.test(String(m || '')));
}

/** Form attachment missing/unreadable — operator must submit a new ticket. */
export function isFfidFormMissing(ticket) {
  const state = getWorkflowState(ticket);
  if (String(state.form_error || '').trim()) return true;
  const missing = state.missing_fields || [];
  return missing.some((m) => /form/i.test(String(m || '')));
}

/** Duration 0 / unusable dates — not blank fields that can be filled on this page. */
export function isFfidUnresolvedWindow(ticket) {
  const state = getWorkflowState(ticket);
  return (
    state.block_reason === 'unresolved_window'
    || state.selection_outcome === 'unresolved_window'
    || state.dates?.error === 'duration_missing'
  );
}

function _ffidDurationIsZero(ticket) {
  const state = getWorkflowState(ticket);
  if (state.dates?.error === 'duration_missing') return true;
  const days = String(state.extracted?.duration_days ?? '').trim();
  return days === '0';
}

/**
 * FFID has no on-page resume form. Never tell the user to fill fields below
 * or that the ticket will restart automatically.
 */
export function getFfidNeedsInfoBanner(ticket) {
  if (isFfidAlreadyAssigned(ticket)) {
    const held = getFfidHeldIds(ticket).join(', ') || 'an existing Firefighter ID';
    const sapUser = String(getWorkflowState(ticket).extracted?.firefighter_sap_user || 'This Firefighter');
    return {
      title: 'Firefighter already has an assigned ID',
      description:
        `${sapUser} already has Firefighter ID ${held}. This request was stopped on purpose. ` +
        'No information is missing — HALO will not assign a second Firefighter ID for an overlapping access window.',
      action: 'Free or expire the existing assignment, then submit a new request if another Firefighter ID is still needed.',
    };
  }
  if (isFfidNoIdAvailable(ticket)) {
    return {
      title: 'No Firefighter ID available',
      description:
        'No Firefighter ID is free for the requested access window. HALO will not invent a new ID or guess.',
      action: 'Free an existing assignment or change the window, then submit a new request.',
    };
  }
  if (isFfidUnresolvedWindow(ticket) || _ffidDurationIsZero(ticket)) {
    const zero = _ffidDurationIsZero(ticket);
    return {
      title: 'Access window cannot be assigned',
      description: zero
        ? 'Duration is 0 days, so HALO has no Valid From / Valid To window. That is not missing information — a 0-day request cannot be assigned.'
        : 'The access dates on this request are incomplete or conflicting, so HALO will not guess a window.',
      action:
        'Review the details below, then submit a new Firefighter ID ticket with a duration of 1 or more days, or explicit Valid From / Valid To dates. This ticket will not resume here.',
    };
  }
  return {
    title: 'Form missing',
    description: 'The Firefighter ID form attachment is missing or unreadable.',
    action:
      'Attach a complete PDF or Word Firefighter ID form to a new ticket and submit again.',
  };
}

/** FFID assigned in SAP but notice could not be sent — human must close. */
export function getFfidNotifyHoldReason(ticket) {
  if (!ticket || resolveWorkflowType(ticket).id !== WORKFLOW_TYPE_FFID) return '';
  const state = getWorkflowState(ticket);
  const reason = String(
    state.escalation?.reason || state.block_reason || state.email_delivery?.reason || '',
  ).trim();
  if (reason === 'missing_notify_email' || reason === 'missing') {
    return 'missing_notify_email';
  }
  if (reason === 'email_send_failed' || reason === 'send_failed') {
    return 'email_send_failed';
  }
  if (
    reason === 'future_window' ||
    String(state.window_kind || '').toLowerCase() === 'future'
  ) {
    const stage = String(ticket.workflow_stage || '').toUpperCase();
    if (
      stage === WORKFLOW_STAGE.ACCESS_ISSUE_REPORTED ||
      stage === WORKFLOW_STAGE.SCHEDULED
    ) {
      return 'future_window';
    }
  }
  const delivery = state.email_delivery && typeof state.email_delivery === 'object'
    ? state.email_delivery
    : {};
  if (String(delivery.status || '') === 'failed' && !String(delivery.to || '').trim()) {
    return 'missing_notify_email';
  }
  if (String(delivery.status || '') === 'failed') {
    return 'email_send_failed';
  }
  const stage = String(ticket.workflow_stage || '').toUpperCase();
  if (
    stage === WORKFLOW_STAGE.SCHEDULED ||
    (stage === WORKFLOW_STAGE.ACCESS_ISSUE_REPORTED &&
      String(state.window_kind || '').toLowerCase() === 'future')
  ) {
    return 'future_window';
  }
  return '';
}

/** FFID holds that the owner closes with a comment (Halo DONE + optional SN resolve). */
export function isFfidHumanCloseHold(ticket) {
  if (!ticket || resolveWorkflowType(ticket).id !== WORKFLOW_TYPE_FFID) return false;
  const stage = String(ticket.workflow_stage || '').toUpperCase();
  if (stage === WORKFLOW_STAGE.ACCESS_ISSUE_REPORTED) return true;
  if (stage === WORKFLOW_STAGE.SCHEDULED && getFfidNotifyHoldReason(ticket) === 'future_window') {
    return true;
  }
  return false;
}

export function getFfidAccessIssueBanner(ticket) {
  const reason = getFfidNotifyHoldReason(ticket);
  if (reason === 'missing_notify_email') {
    return {
      title: 'Human escalation — email missing',
      description:
        'Firefighter ID is assigned in SAP, but no email address was on the ticket. Notification was not sent. Contact the Firefighter personally, add a comment, and mark resolved — or close the incident in ServiceNow. This ticket is not complete until someone closes it.',
    };
  }
  if (reason === 'email_send_failed') {
    return {
      title: 'Human escalation — email sending failed',
      description:
        'Firefighter ID is assigned in SAP, but the notification email could not be sent. Contact the Firefighter personally, add a comment, and mark resolved — or close the incident in ServiceNow. This ticket is not complete until someone closes it.',
    };
  }
  if (reason === 'future_window') {
    return {
      title: 'Human escalation — waiting for access date',
      description:
        'The Firefighter ID is reserved in GRC. HALO will not email again on Valid From. When the access date arrives, confirm EAM visibility, enter a comment, and mark complete. That also resolves ServiceNow when this ticket came from ServiceNow.',
    };
  }
  return {
    title: 'Human escalation — access issue',
    description:
      'The end user reported an issue with the assigned Firefighter ID. A human must investigate, then mark resolved to complete the Halo ticket (and resolve ServiceNow when applicable).',
  };
}

/** Soft holds (ops follow-up) stay amber; end-user-reported access issues are critical. */
export function isFfidSoftHumanHold(ticket) {
  const hold = getFfidNotifyHoldReason(ticket);
  return (
    hold === 'missing_notify_email' ||
    hold === 'email_send_failed' ||
    hold === 'future_window'
  );
}

export function isFfidUserReportedAccessIssue(ticket) {
  if (!ticket || resolveWorkflowType(ticket).id !== WORKFLOW_TYPE_FFID) return false;
  const stage = String(ticket.workflow_stage || '').toUpperCase();
  const uc = getUserConfirmation(ticket);
  const response = String(uc?.response || uc?.status || '').toUpperCase();
  if (response === 'ISSUE' || response === 'ISSUE_REPORTED') return true;
  if (stage !== WORKFLOW_STAGE.ACCESS_ISSUE_REPORTED) return false;
  return !isFfidSoftHumanHold(ticket);
}

export function getFfidNotificationEmailDisplay(ticket) {
  const reason = getFfidNotifyHoldReason(ticket);
  const email = getNotificationEmail(ticket);
  const status = getNotificationEmailStatus(ticket);
  if (reason === 'missing_notify_email') {
    return { value: 'Email ID missing', missing: true };
  }
  if (reason === 'email_send_failed') {
    return {
      value: email ? `${email} — email sending failed` : 'Email sending failed',
      missing: true,
    };
  }
  if (email && status === 'sent') {
    const kind = String(getWorkflowState(ticket).provisioning?.email?.kind || '').toLowerCase();
    const suffix = kind === 'reservation' ? 'reservation sent' : 'sent';
    return { value: `${email} (${suffix})`, missing: false };
  }
  return { value: email || null, missing: false };
}

const TER_ESCALATION_FALLBACK =
  'Post-termination validation could not confirm the user is fully deprovisioned. Verify SAP account state and remediate manually.';
const FFID_ESCALATION_FALLBACK =
  'No available Firefighter ID satisfies the requested T-codes. Security must review FFID configuration. HALO will not add roles or T-codes.';
const GENERIC_ESCALATION_FALLBACK = 'This request was escalated for human review.';

/** Escalation banner/toast copy. Never reuse termination text on FFID tickets. */
export function getWorkflowEscalationMessage(ticket) {
  const state = getWorkflowState(ticket);
  const explicit = String(
    state.escalation?.message || state.validation?.message || state.validation?.reason || '',
  ).trim();
  const type = resolveWorkflowType(ticket);
  if (explicit) {
    const looksLikeTer =
      /deprovision|post-termination|offboarding/i.test(explicit);
    if (type.id === WORKFLOW_TYPE_FFID && looksLikeTer) {
      return FFID_ESCALATION_FALLBACK;
    }
    return explicit;
  }
  if (type.id === WORKFLOW_TYPE_FFID) return FFID_ESCALATION_FALLBACK;
  if (type.id === WORKFLOW_TYPE_TER) return TER_ESCALATION_FALLBACK;
  return GENERIC_ESCALATION_FALLBACK;
}

export function getNotificationEmail(ticket) {
  const state = getWorkflowState(ticket);
  const delivery = state.email_delivery && typeof state.email_delivery === 'object'
    ? state.email_delivery
    : {};
  const fromProvisioning = state.provisioning?.email;
  const provisionedTo =
    fromProvisioning && typeof fromProvisioning === 'object'
      ? fromProvisioning.to
      : fromProvisioning;
  return String(
    delivery.to
      || state.notification_email
      || provisionedTo
      || state.extracted?.email
      || '',
  ).trim();
}

export function getNotificationEmailStatus(ticket) {
  const state = getWorkflowState(ticket);
  const delivery = state.email_delivery && typeof state.email_delivery === 'object'
    ? state.email_delivery
    : {};
  if (delivery.status) return String(delivery.status);
  if (state.provisioning?.email?.status === 'ok' || state.provisioning?.email?.to) {
    return 'sent';
  }
  return state.notification_email ? 'resolved' : '';
}

export function needsInfo(ticket) {
  if ((ticket?.workflow_stage || '') !== WORKFLOW_STAGE.NEEDS_INFO) return false;
  if (isFfidAlreadyAssigned(ticket)) return false;
  return true;
}

export function getMissingFields(ticket) {
  const state = getWorkflowState(ticket);
  if (isFfidAlreadyAssigned(ticket)) return [];
  return Array.isArray(state.missing_fields) ? state.missing_fields : [];
}

export function getWorkflowState(ticket) {
  const state = ticket?.workflow_state;
  if (state && typeof state === 'object') return state;
  if (typeof state === 'string') {
    try {
      const parsed = JSON.parse(state);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

export function getWorkflowSteps(ticket) {
  const state = getWorkflowState(ticket);
  if (Array.isArray(state.steps) && state.steps.length > 0) {
    const type = resolveWorkflowType(ticket);
    if (type.id === 'new_user_provisioning') return normalizeNupSteps(state.steps);
    return state.steps;
  }
  const type = resolveWorkflowType(ticket);
  if (type.id === 'new_user_provisioning') return defaultNupSteps();
  if (type.id === WORKFLOW_TYPE_PAMTER) {
    return [
      { key: 'parse_request', label: 'Parse request', status: 'pending' },
      { key: 'lookup_user', label: 'Lookup user & mailbox', status: 'pending' },
      { key: 'check_litigation_hold', label: 'Check litigation hold', status: 'pending' },
      { key: 'convert_mailbox', label: 'Convert mailbox to shared', status: 'pending' },
      { key: 'cleanup_and_disable', label: 'Clean up groups/licenses & disable', status: 'pending' },
      { key: 'complete', label: 'Complete', status: 'pending' },
    ];
  }
  if (type.id === 'user_termination') {
    const mode =
      state.termination_mode ||
      state.extracted?.termination_mode ||
      null;
    return defaultTerSteps(mode);
  }
  if (type.id === WORKFLOW_TYPE_PWD_RESET) return defaultPwdResetSteps();
  if (type.id === WORKFLOW_TYPE_FFID) return defaultFfidSteps();
  return [];
}

/** Steps shown on the ticket React Flow. FFID collapses technical children into business milestones. */
export function getWorkflowDisplaySteps(ticket) {
  const steps = getWorkflowSteps(ticket);
  if (resolveWorkflowType(ticket).id !== WORKFLOW_TYPE_FFID) return steps;
  const state = getWorkflowState(ticket);
  return collapseFfidSteps(steps, {
    currentStep: state.current_step,
    workflowStage: ticket?.workflow_stage,
    windowKind: state.window_kind,
    notifyHold: getFfidNotifyHoldReason(ticket),
  });
}

export function getWorkflowDisplayCurrentStep(ticket) {
  const state = getWorkflowState(ticket);
  const raw = state.current_step;
  if (resolveWorkflowType(ticket).id !== WORKFLOW_TYPE_FFID) return raw;
  if (String(ticket?.workflow_stage || '').toUpperCase() === WORKFLOW_STAGE.DONE) {
    return 'complete';
  }
  if (isFfidHumanCloseHold(ticket) && getFfidNotifyHoldReason(ticket) === 'future_window') {
    return 'await_user_confirmation';
  }
  return ffidMilestoneKeyForStep(raw);
}

export function getApproval(ticket) {
  const state = getWorkflowState(ticket);
  return state.approval && typeof state.approval === 'object'
    ? state.approval
    : { status: 'pending', by: null, at: null };
}

/** Add/remove role delta from workflow_state (Access Modification workflow). */
export function getWorkflowRoleDelta(ticket) {
  const state = getWorkflowState(ticket);
  const delta = state.role_delta;
  if (!delta || typeof delta !== 'object') return { add: [], remove: [] };
  return {
    add: Array.isArray(delta.add) ? delta.add : [],
    remove: Array.isArray(delta.remove) ? delta.remove : [],
  };
}

/** Roles listed for copy in workflow_state (NUP / role-copy workflows). */
export function getWorkflowRoles(ticket) {
  const state = getWorkflowState(ticket);
  const roles = state.reference_roles || state.roles || state.roles_to_copy;
  if (Array.isArray(roles)) return roles;

  const { add, remove } = getWorkflowRoleDelta(ticket);
  if (add.length > 0 || remove.length > 0) {
    return [...add.map((r) => `+ ${r}`), ...remove.map((r) => `- ${r}`)];
  }
  return [];
}

/** SoD payload from workflow_state. */
export function getWorkflowSod(ticket) {
  const state = getWorkflowState(ticket);
  const sod = state.sod || state.sod_result || {};
  return sod && typeof sod === 'object' ? sod : {};
}

export function getExtractedWorkflowFields(ticket) {
  const state = getWorkflowState(ticket);
  const extracted = state.extracted || {};
  return extracted && typeof extracted === 'object' ? extracted : {};
}

function humanizeKey(key) {
  return String(key || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function ffidApprovalOverlay(state) {
  const dates = state.dates || {};
  const proposed = state.proposed || {};
  const check = state.tcode_check || {};
  const window =
    dates.valid_from && dates.valid_to ? `${dates.valid_from} → ${dates.valid_to}` : null;
  let tcodeLabel = null;
  if (check.performed) {
    tcodeLabel =
      check.status === 'ok'
        ? 'OK — requested T-codes present'
        : check.status === 'configuration_exception'
          ? `Exception — missing ${(check.missing || []).join(', ')}`
          : String(check.status || '');
  } else if (check.status === 'skipped') {
    tcodeLabel = `Skipped (${check.reason || 'n/a'})`;
  }
  const availability = proposed.availability
    ? `${proposed.availability}${proposed.occupancy ? ` (${proposed.occupancy})` : ''}`
    : null;
  return {
    access_window: window,
    proposed_ffid: proposed.ffid || null,
    availability,
    tcode_check: tcodeLabel,
  };
}

/**
 * Compact approval-board summary for any workflow ticket.
 * Type-specific chrome comes from `resolveWorkflowType`; unknown types still work.
 */
export function summarizeWorkflowForApproval(ticket) {
  const type = resolveWorkflowType(ticket);
  const extracted = getExtractedWorkflowFields(ticket);
  const roles = getWorkflowRoles(ticket);
  const sod = getWorkflowSod(ticket);
  const approval = getApproval(ticket);
  const state = getWorkflowState(ticket);
  const overlay = type.id === WORKFLOW_TYPE_FFID ? ffidApprovalOverlay(state) : {};
  const conflicts = Array.isArray(sod.conflicts) ? sod.conflicts : [];
  const sodSkipped =
    Boolean(sod.skipped) ||
    sod.status === 'disabled' ||
    state.sod_enabled === false;
  const summaryText = typeof state.summary === 'string' ? state.summary : '';

  const primaryFields =
    type.primaryFields.length > 0
      ? type.primaryFields.map((f) => ({
          key: f.key,
          label: f.label,
          value:
            overlay[f.key] ??
            extracted[f.key] ??
            extracted[f.key.replace(/_/g, '')] ??
            null,
        }))
      : Object.entries(extracted)
          .filter(([, v]) => v != null && String(v).trim() !== '')
          .slice(0, 6)
          .map(([key, value]) => ({ key, label: humanizeKey(key), value }));

  const subjectKey = type.subjectField;
  const subject =
    (subjectKey && (extracted[subjectKey] || null)) ||
    primaryFields[0]?.value ||
    null;

  return {
    ticketNumber: ticket?.ticket_number || ticket?.id || '',
    title: ticket?.title || type.label,
    description: ticket?.description || type.description,
    stage: ticket?.workflow_stage || null,
    workflowType: type,
    primaryFields,
    subject,
    // NUP-shaped aliases kept for existing callers
    newUser: extracted.new_user || extracted.user || null,
    email: extracted.email || null,
    referenceUser: extracted.reference_user || extracted.referenceuser || null,
    roles,
    roleCount: roles.length,
    roleDelta: getWorkflowRoleDelta(ticket),
    showsRoles: Boolean(type.showsRoles),
    showsSod: Boolean(type.showsSod),
    sodSkipped,
    sodConflictCount: conflicts.length,
    sodConflicts: conflicts.slice(0, 5),
    hasSodConflicts: conflicts.length > 0,
    approval,
    summaryPreview: summaryText.slice(0, 400),
    updatedAt: ticket?.updated_at || ticket?.created_at || null,
    approveVerb: type.approveVerb,
    rejectVerb: type.rejectVerb,
  };
}

export function isWorkflowDecided(ticket) {
  const stage = ticket?.workflow_stage || '';
  // Post-decision stages (approval ran; terminal outcome or post-provision NUP issue).
  return (
    stage === WORKFLOW_STAGE.DONE ||
    stage === WORKFLOW_STAGE.REJECTED ||
    stage === WORKFLOW_STAGE.FAILED ||
    stage === WORKFLOW_STAGE.ESCALATED ||
    stage === WORKFLOW_STAGE.ACCESS_ISSUE_REPORTED
  );
}

/**
 * Optimistic UI helper only — prefer POST /workflow/{tn}/action so the backend
 * runs real MCP provisioning (create_user → copy_roles).
 */
export function computeApprovalTransition(ticket, { approved, approver, comment }) {
  const state = { ...getWorkflowState(ticket) };
  const steps = getWorkflowSteps(ticket).map((s) => ({ ...s }));
  const setStatus = (key, status) => {
    const step = steps.find((s) => s.key === key);
    if (step) step.status = status;
  };
  const type = resolveWorkflowType(ticket);
  const isTer = type.id === 'user_termination';

  const approval = { ...getApproval(ticket) };
  approval.by = approver || approval.by || 'Current User';
  approval.at = new Date().toISOString();
  if (comment) approval.comment = comment;

  let stage;
  if (approved) {
    approval.status = 'approved';
    setStatus('await_approval', 'done');
    if (isTer) {
      setStatus('terminate_user', 'active');
      state.current_step = 'terminate_user';
      stage = WORKFLOW_STAGE.TERMINATING;
    } else {
      setStatus('create_user', 'active');
      setStatus('provision', 'active');
      state.current_step = 'create_user';
      stage = WORKFLOW_STAGE.PROVISIONING;
    }
  } else {
    approval.status = 'rejected';
    setStatus('await_approval', 'done');
    const skipKeys = isTer
      ? ['terminate_user', 'validate_termination', 'complete']
      : ['provision', 'create_user', 'copy_roles', 'send_email', 'complete'];
    skipKeys.forEach((k) => setStatus(k, 'skipped'));
    state.current_step = 'await_approval';
    stage = WORKFLOW_STAGE.REJECTED;
  }

  state.steps = steps;
  state.approval = approval;
  return { workflow_stage: stage, workflow_state: state };
}
